/**
 * Offline Storage Service
 * Manages IndexedDB for storing downloaded audio files and metadata
 */

export interface DownloadedStream {
  streamId: string;
  name: string;
  description: string;
  genre: string;
  coverImage?: string;
  audioBlob: Blob;
  audioUrl: string;
  bitrate: number;
  format: string;
  fileSize: number;
  downloadedAt: number;
  lastPlayedAt?: number;
}

export interface DownloadProgress {
  streamId: string;
  loaded: number;
  total: number;
  percentage: number;
  status: 'downloading' | 'completed' | 'failed';
}

const DB_NAME = 'tempest-radio-offline';
const DB_VERSION = 1;
const AUDIO_STORE = 'downloaded-streams';
const MAX_STORAGE_MB = 500; // Maximum storage limit in MB

class OfflineStorageService {
  private db: IDBDatabase | null = null;

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(new Error('Failed to open IndexedDB'));

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store for downloaded streams
        if (!db.objectStoreNames.contains(AUDIO_STORE)) {
          const store = db.createObjectStore(AUDIO_STORE, { keyPath: 'streamId' });
          store.createIndex('downloadedAt', 'downloadedAt', { unique: false });
          store.createIndex('lastPlayedAt', 'lastPlayedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Download and save a stream for offline playback
   */
  async downloadStream(
    streamId: string,
    audioUrl: string,
    metadata: {
      name: string;
      description: string;
      genre: string;
      coverImage?: string;
      bitrate: number;
      format: string;
    },
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    // Check if stream is already downloaded
    const existing = await this.getDownloadedStream(streamId);
    if (existing) {
      throw new Error('Stream already downloaded');
    }

    // Check storage quota
    const storageEstimate = await navigator.storage?.estimate();
    if (storageEstimate) {
      const usedMB = (storageEstimate.usage || 0) / (1024 * 1024);
      if (usedMB > MAX_STORAGE_MB) {
        throw new Error('Storage limit exceeded. Please delete some downloads.');
      }
    }

    // Download the audio file
    const response = await fetch(audioUrl);
    if (!response.ok) {
      throw new Error('Failed to download audio file');
    }

    const contentLength = parseInt(response.headers.get('content-length') || '0');
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Failed to read response body');
    }

    const chunks: Uint8Array[] = [];
    let receivedLength = 0;

    // Read stream and track progress
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      if (onProgress) {
        onProgress({
          streamId,
          loaded: receivedLength,
          total: contentLength,
          percentage: contentLength ? (receivedLength / contentLength) * 100 : 0,
          status: 'downloading'
        });
      }
    }

    // Combine chunks into a single blob
    const audioBlob = new Blob(chunks as BlobPart[], { type: 'audio/mpeg' });

    // Save to IndexedDB
    const downloadedStream: DownloadedStream = {
      streamId,
      ...metadata,
      audioBlob,
      audioUrl,
      fileSize: receivedLength,
      downloadedAt: Date.now()
    };

    await this.saveDownloadedStream(downloadedStream);

    if (onProgress) {
      onProgress({
        streamId,
        loaded: receivedLength,
        total: contentLength,
        percentage: 100,
        status: 'completed'
      });
    }
  }

  /**
   * Save a downloaded stream to IndexedDB
   */
  private async saveDownloadedStream(stream: DownloadedStream): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([AUDIO_STORE], 'readwrite');
      const store = transaction.objectStore(AUDIO_STORE);
      const request = store.put(stream);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save stream'));
    });
  }

  /**
   * Get a downloaded stream by ID
   */
  async getDownloadedStream(streamId: string): Promise<DownloadedStream | null> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([AUDIO_STORE], 'readonly');
      const store = transaction.objectStore(AUDIO_STORE);
      const request = store.get(streamId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error('Failed to get stream'));
    });
  }

  /**
   * Get all downloaded streams
   */
  async getAllDownloadedStreams(): Promise<DownloadedStream[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([AUDIO_STORE], 'readonly');
      const store = transaction.objectStore(AUDIO_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get streams'));
    });
  }

  /**
   * Delete a downloaded stream
   */
  async deleteDownloadedStream(streamId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([AUDIO_STORE], 'readwrite');
      const store = transaction.objectStore(AUDIO_STORE);
      const request = store.delete(streamId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete stream'));
    });
  }

  /**
   * Update last played timestamp for a stream
   */
  async updateLastPlayed(streamId: string): Promise<void> {
    const stream = await this.getDownloadedStream(streamId);
    if (stream) {
      stream.lastPlayedAt = Date.now();
      await this.saveDownloadedStream(stream);
    }
  }

  /**
   * Get total storage used by downloaded streams
   */
  async getStorageUsed(): Promise<{ bytes: number; megabytes: number }> {
    const streams = await this.getAllDownloadedStreams();
    const bytes = streams.reduce((total, stream) => total + stream.fileSize, 0);
    return {
      bytes,
      megabytes: bytes / (1024 * 1024)
    };
  }

  /**
   * Check if a stream is downloaded
   */
  async isStreamDownloaded(streamId: string): Promise<boolean> {
    const stream = await this.getDownloadedStream(streamId);
    return stream !== null;
  }

  /**
   * Get object URL for downloaded stream
   */
  async getOfflineStreamUrl(streamId: string): Promise<string | null> {
    const stream = await this.getDownloadedStream(streamId);
    if (!stream) return null;

    // Create blob URL from stored blob
    return URL.createObjectURL(stream.audioBlob);
  }

  /**
   * Clear all downloaded streams
   */
  async clearAllDownloads(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([AUDIO_STORE], 'readwrite');
      const store = transaction.objectStore(AUDIO_STORE);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear downloads'));
    });
  }

  /**
   * Get storage quota information
   */
  async getStorageQuota(): Promise<{
    available: number;
    used: number;
    total: number;
    percentage: number;
  }> {
    const estimate = await navigator.storage?.estimate();
    const usage = estimate?.usage || 0;
    const quota = estimate?.quota || 0;

    return {
      used: usage,
      available: quota - usage,
      total: quota,
      percentage: quota ? (usage / quota) * 100 : 0
    };
  }
}

// Export singleton instance
const offlineStorage = new OfflineStorageService();
export default offlineStorage;
